import type { Middleware } from '@reduxjs/toolkit';
import { openRecipeAction } from './openRecipeReducer';
import { showGlobalNotification } from '@src/features/interface/interfaceSlice';
import { getDefaultRecipe } from '@src/features/interface/interfaceApi';
import { RecipeType } from '@kemu-io/kemu-types';

/**
 * Handles openRecipeAction.rejected by loading the default recipe
 * and showing a notification with the error message.
 */
export const openRecipeFailureMiddleware: Middleware = (storeApi) => (next) => (action) => {
	const result = next(action);

	if (action.type === openRecipeAction.rejected.type) {
		const state = storeApi.getState() as {
			user: {
				currentUserProfile: {
					profile?: {
						id: string;
						firstName: string;
						lastName?: string;
					};
				};
			};
			workspace: {
				currentRecipe: {
					type?: RecipeType;
				};
			};
		};
		const { user, workspace } = state;

		// Get error message from the rejected action
		const errorMessage = action.error?.message 
			|| (typeof action.payload === 'string' ? action.payload : undefined)
			|| 'Failed to open recipe';

		// Show notification with error
		storeApi.dispatch(showGlobalNotification({
			title: 'Failed to open recipe',
			message: errorMessage,
			type: 'error',
		}));

		// Only try to load default recipe if user profile is available
		if (user.currentUserProfile.profile) {
			// Get recipe type from current workspace or default to Browser
			const recipeType = workspace.currentRecipe.type || RecipeType.Browser;

			// Create and open default recipe
			getDefaultRecipe({
				userId: user.currentUserProfile.profile.id,
				firstName: user.currentUserProfile.profile.firstName,
				lastName: user.currentUserProfile.profile.lastName,
			}).then((defaultRecipe) => {
				// Type assertion needed because middleware dispatch doesn't always recognize thunk actions
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(storeApi.dispatch as any)(openRecipeAction({
					recipe: defaultRecipe,
					isDefault: true,
					dbInfo: {
						id: '',
						authorId: user.currentUserProfile.profile!.id,
						version: 0,
						recipeType: recipeType,
					},
				}));
			}).catch((err) => {
				console.error('Failed to create default recipe after recipe open failure:', err);
				storeApi.dispatch(showGlobalNotification({
					title: 'Error',
					message: 'Failed to load default recipe. Please refresh the page.',
					type: 'error',
				}));
			});
		}
	}

	return result;
};

export default openRecipeFailureMiddleware;

